import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CircleUser } from "lucide-react";
import Link from "next/link";
import useAuthState from "@/src/hooks/use-auth-state";
import { cn } from "@/src/lib/utils";
import { Button } from "../ui/button";
import SignOutDialog from "../dialogBox/SignOutDialog";
import { useOnClickOutside } from "@/src/hooks/use-on-click-outside";
import { Paragraph } from "./Paragraph";

const MyAccountDropDown = () => {
  return (
    <div className="z-[1] flex justify-end">
      <FlyoutLink FlyoutContent={PricingContent}>
        <div className="flex flex-col items-center gap-1 text-black">
          <div className="cursor-pointer font-light hover:text-main-default">
            <CircleUser className="sm:h-7 sm:w-7 md:h-6 md:w-6" />
          </div>
          <div className="hidden text-nowrap text-xs font-light md:block">
            My Account
          </div>
        </div>
      </FlyoutLink>
    </div>
  );
};

interface FlyoutLinkInterface {
  children: React.ReactNode;
  FlyoutContent: any;
}

const FlyoutLink = ({ children, FlyoutContent }: FlyoutLinkInterface) => {
  const [open, setOpen] = useState(false);

  const showFlyout = FlyoutContent && open;

  const [isSmallScreen, setIsSmallScreen] = useState(false);

  // Check if the screen is small (e.g., xs breakpoint)
  useEffect(() => {
    const updateScreenSize = () => {
      setIsSmallScreen(window.innerWidth < 640); // Adjust to match Tailwind's `sm` breakpoint (640px)
    };

    updateScreenSize(); // Initial check
    window.addEventListener("resize", updateScreenSize);

    return () => {
      window.removeEventListener("resize", updateScreenSize);
    };
  }, []);

  const navRef = useRef<HTMLDivElement | null>(null);
  //For outside click control dropdown
  useOnClickOutside(navRef, () => setOpen(false));

  return (
    <div
      ref={navRef}
      onMouseEnter={() => {
        if (!isSmallScreen) setOpen(true);
      }}
      onMouseLeave={() => {
        if (!isSmallScreen) setOpen(false);
      }}
      onClick={() => {
        if (isSmallScreen) setOpen(!open);
      }}
      className="relative h-fit w-fit"
    >
      <Paragraph className="relative text-white">
        {children}
        {/* <span
                    style={{
                        transform: showFlyout ? "scaleX(1)" : "scaleX(0)",
                    }}
                    className="absolute -bottom-2 -left-2 -right-2 h-1 origin-left scale-x-0 rounded-full bg-indigo-300 transition-transform duration-300 ease-out"
                /> */}
      </Paragraph>
      <AnimatePresence>
        {showFlyout && (
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 15 }}
            style={{ translateX: "-50%" }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="absolute left-1/2 top-14 z-50 rounded-2xl bg-white text-black"
          >
            <div className="absolute -top-6 left-0 right-0 h-6 bg-transparent" />
            {/* <div className="absolute left-1/2 top-0 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rotate-45 " /> */}
            <div className="absolute left-1/2 top-0 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rotate-45 bg-white shadow-[-1px_-1px_0px_0px_rgba(0,0,0,0.1)]" />

            <FlyoutContent />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const PricingContent = () => {
  const { user, loading } = useAuthState();

  return (
    <div
      className={cn(
        "border-1 w-40 rounded-2xl border p-6 shadow-[-0.5px_-0.5px_0px_2px_rgba(0,0,0,0.1)]",
        `${user ? "w-60" : "w-40"}`,
      )}
    >
      {loading && <div className="text-sm text-gray-400">Loading...</div>}

      {!user && !loading && (
        <>
          <div className="mb-3 space-y-3">
            <Link
              href="/u/register"
              className="block text-sm font-semibold text-gray-400"
            >
              Register
            </Link>
          </div>
          <div className="mb-6 space-y-3">
            <Link
              href="/u/sign-in"
              className="block text-sm font-semibold text-gray-400"
            >
              Sign in
            </Link>
          </div>
        </>
      )}

      {user && !loading && (
        <>
          <div className="whitespace-wrap mb-2 w-full text-wrap break-words text-sm text-gray-500">
            {"Email"}
          </div>
          <div className="whitespace-wrap w-full text-wrap break-words text-xs">
            {user?.email}
          </div>

          <div className="my-4 h-[0.5px] bg-slate-400"></div>
          <div className="mb-6 space-y-3">
            {/* <Button className="text-sm font-semibold text-white w-full flex">
                                Logout
                            </Button> */}
            <SignOutDialog />
          </div>
        </>
      )}
    </div>
  );
};

export default MyAccountDropDown;
